<template>
  <div class="app-layout">
    <Sidebar />
    <div class="lesson-create-container">
      <div class="create-header">
        <h2>Create New Lesson</h2>
        <button @click="$router.go(-1)" class="back-button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Lessons
        </button>
      </div>

      <form @submit.prevent="submitForm" class="lesson-form">
        <div class="form-group" :class="{ 'has-error': errors.title }">
          <label>Title <span class="required">*</span></label>
          <EditorContent :editor="titleEditor" class="editor-title" />
          <span v-if="errors.title" class="error-message">{{ errors.title }}</span>
        </div>

        <div class="form-group" :class="{ 'has-error': errors.title_ar }">
          <label>Title (Arabic) <span class="required">*</span></label>
          <input 
            v-model="form.title_ar"
            type="text" 
            placeholder="Enter title in Arabic"
            class="form-input"
            dir="rtl"
          />
          <span v-if="errors.title_ar" class="error-message">{{ errors.title_ar }}</span>
        </div>

        <div class="form-group" :class="{ 'has-error': errors.description }">
          <label>Content (Rich Text) <span class="required">*</span></label>
          <div class="editor-container">
            <EditorContent :editor="descriptionEditor" />
            <MenuBar v-if="descriptionEditor" class="editor-menu" :editor="descriptionEditor">
              <button :class="{ 'is-active': descriptionEditor.isActive('bold') }" @click="descriptionEditor.chain().focus().toggleBold().run()">
                <i class="fas fa-bold"></i>
              </button>
              <button :class="{ 'is-active': descriptionEditor.isActive('italic') }" @click="descriptionEditor.chain().focus().toggleItalic().run()">
                <i class="fas fa-italic"></i>
              </button>
              <button :class="{ 'is-active': descriptionEditor.isActive('underline') }" @click="descriptionEditor.chain().focus().toggleUnderline().run()">
                <i class="fas fa-underline"></i>
              </button>
              <button @click="descriptionEditor.chain().focus().toggleBulletList().run()">
                <i class="fas fa-list"></i>
              </button>
            </MenuBar>
          </div>
          <span v-if="errors.description" class="error-message">{{ errors.description }}</span>
        </div>

        <div class="form-group" :class="{ 'has-error': errors.description }">
          <label>Description (Short Summary) <span class="required">*</span></label>
          <input 
            v-model="form.description"
            type="text" 
            placeholder="Enter a brief description"
            class="form-input"
          />
          <span v-if="errors.description" class="error-message">{{ errors.description }}</span>
        </div>

        <div class="form-group" :class="{ 'has-error': errors.description_ar }">
          <label>Description (Arabic) <span class="required">*</span></label>
          <input 
            v-model="form.description_ar"
            type="text" 
            placeholder="Enter description in Arabic"
            class="form-input"
            dir="rtl"
          />
          <span v-if="errors.description_ar" class="error-message">{{ errors.description_ar }}</span>
        </div>

        <div class="form-group" :class="{ 'has-error': errors.course_id }">
          <label>Course <span class="required">*</span></label>
          <v-select
            v-model="form.course_id"
            :options="filteredCourses"
            label="title"
            :reduce="course => course.id"
            placeholder="Search courses..."
            class="course-selector"
            :class="{ 'is-invalid': errors.course_id }"
          >
            <template #no-options="{ search }">
              <div class="text-gray-500 py-2 px-4">
                No courses found for "{{ search }}"
              </div>
            </template>
          </v-select>
          <span v-if="errors.course_id" class="error-message">{{ errors.course_id }}</span>
        </div>

        <div class="form-group" :class="{ 'has-error': errors.order }">
          <label>Order <span class="required">*</span></label>
          <input 
            v-model="form.order" 
            type="number" 
            min="1"
            class="form-input"
            placeholder="Enter lesson order"
          />
          <span v-if="errors.order" class="error-message">{{ errors.order }}</span>
        </div>

        <div class="form-group" :class="{ 'has-error': errors.video }">
          <label>Video <span class="required">*</span></label>
          <v-select
            v-model="selectedVideo"
            :options="filteredVideos"
            label="title"
            :reduce="video => video"
            placeholder="Search videos..."
            class="video-selector"
            :class="{ 'is-invalid': errors.video }"
            @input="updateVideoFields"
          >
            <template #option="video">
              <div class="video-option">
                <img :src="video.assets.thumbnail" class="video-thumbnail" />
                <div>
                  <strong>{{ video.title }}</strong>
                  <div class="video-details">
                    <span>Duration: {{ formatDuration(video.duration) }}</span>
                    <span>MP4: {{ video.assets.mp4 }}</span>
                  </div>
                </div>
              </div>
            </template>
            <template #no-options="{ search }">
              <div class="text-gray-500 py-2 px-4">
                No videos found for "{{ search }}"
              </div>
            </template>
          </v-select>
          <span v-if="errors.video" class="error-message">{{ errors.video }}</span>
        </div>

        <div class="form-actions">
          <button type="button" @click="clearForm" class="clear-button">
            <i class="fas fa-trash-alt"></i> Clear All
          </button>
          <button type="button" @click="$router.go(-1)" class="cancel-button">
            <i class="fas fa-times"></i> Cancel
          </button>
          <button type="submit" :disabled="isSubmitting" class="submit-button">
            <i class="fas fa-save"></i> {{ isSubmitting ? 'Creating...' : 'Create Lesson' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'

export default {
  name: 'LessonCreate',
  components: {
    Sidebar,
    vSelect,
    EditorContent
  },
  data() {
    return {
      selectedVideo: null,
      form: {
        title: '',
        description: '',
        course_id: '',
        lesson_video_id: '',
        lesson_url: '',
        lesson_thumbnail_url: '',
        lesson_status: 'uploaded',
        order: 1,
        duration: 3600,
        is_hidden: 0,
        downloadable_url: '',
        premium_course_id: '',
        title_ar: '',
        description_ar: '',
      },
      errors: {
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        course_id: '',
        order: '',
        video: ''
      },
      courses: [],
      videos: [],
      isSubmitting: false,
      courseSearchTerm: '',
      videoSearchTerm: '',
      titleEditor: null,
      descriptionEditor: null
    };
  },
  computed: {
    filteredCourses() {
      return this.courses.filter(course => 
        course.title.toLowerCase().includes(this.courseSearchTerm.toLowerCase())
      );
    },
    filteredVideos() {
      return this.videos.filter(video => 
        video.title.toLowerCase().includes(this.videoSearchTerm.toLowerCase()) ||
        (video.description && video.description.toLowerCase().includes(this.videoSearchTerm.toLowerCase())) ||
        (video.assets?.mp4 && video.assets.mp4.toLowerCase().includes(this.videoSearchTerm.toLowerCase()))
      );
    }
  },
  created() {
    this.fetchCourses();
    this.fetchApiVideos();
  },
  mounted() {
    this.titleEditor = new Editor({
      content: this.form.title,
      extensions: [StarterKit],
      onUpdate: () => {
        this.form.title = this.titleEditor.isEmpty ? '' : this.titleEditor.getHTML()
      }
    })

    this.descriptionEditor = new Editor({
      content: this.form.description,
      extensions: [StarterKit, Underline],
      onUpdate: () => {
        this.form.description = this.descriptionEditor.isEmpty ? '' : this.descriptionEditor.getHTML()
      }
    })
  },
  beforeUnmount() {
    this.titleEditor.destroy()
    this.descriptionEditor.destroy()
  },
  methods: {
    async fetchCourses() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('https://api.kewaar.com/api/admin/courses', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        this.courses = response.data.data
          .map(course => ({
            id: course.id,
            title: course.title
          }))
          .sort((a, b) => a.title.localeCompare(b.title));
      } catch (error) {
        console.error('Error fetching courses:', error);
        alert(`Failed to load courses: ${error.response?.data?.message || error.message}`);
      }
    },
    async fetchApiVideos() {
      try {
        const authResponse = await axios.post('https://ws.api.video/auth/api-key', {
          apiKey: "LXcWKZfW4Kn45Y9Z3v5IdNYl1btN228g0sSVSG2hAOL"
        });
        
        const token = authResponse.data.access_token;
        let allVideos = [];
        let currentPage = 1;
        let totalPages = 1;

        do {
          const videosResponse = await axios.get('https://ws.api.video/videos', {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            params: {
              currentPage: currentPage,
              pageSize: 100 // Maximum allowed per page
            }
          });

          allVideos = allVideos.concat(videosResponse.data.data);
          totalPages = videosResponse.data.pagination.pagesTotal;
          currentPage++;
        } while (currentPage <= totalPages);

        this.videos = allVideos.map(video => ({
          videoId: video.videoId,
          title: video.title,
          description: video.description,
          assets: video.assets,
          duration: video.duration
        })).sort((a, b) => a.title.localeCompare(b.title));
      } catch (error) {
        console.error('Error fetching videos:', error);
        alert('Failed to load video list');
      }
    },
    updateVideoFields(selectedVideo) {
      if (selectedVideo?.assets) {
        this.form.lesson_video_id = selectedVideo.assets?.player || '';
        this.form.lesson_url = selectedVideo.assets?.hls || '';
        this.form.lesson_thumbnail_url = selectedVideo.assets?.thumbnail || '';
        this.form.downloadable_url = selectedVideo.assets?.mp4 || '';
        this.form.duration = selectedVideo.duration || 3600;
      } else {
        this.form.lesson_video_id = '';
        this.form.lesson_url = '';
        this.form.lesson_thumbnail_url = '';
        this.form.downloadable_url = '';
        this.form.duration = 3600;
      }
    },
    formatDuration(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    async submitForm() {
      try {
        // Reset errors
        Object.keys(this.errors).forEach(key => this.errors[key] = '');
        
        // Validate form
        let hasErrors = false;
        
        const title = this.titleEditor.getText().trim();
        if (!title) {
          this.errors.title = 'Title is required';
          hasErrors = true;
        }
        
        if (!this.form.title_ar) {
          this.errors.title_ar = 'Arabic title is required';
          hasErrors = true;
        }
        
        if (!this.form.description) {
          this.errors.description = 'Description is required';
          hasErrors = true;
        }
        
        if (!this.form.description_ar) {
          this.errors.description_ar = 'Arabic description is required';
          hasErrors = true;
        }
        
        if (!this.form.course_id) {
          this.errors.course_id = 'Course selection is required';
          hasErrors = true;
        }
        
        if (!this.form.order || this.form.order < 1) {
          this.errors.order = 'Valid order number is required';
          hasErrors = true;
        }
        
        if (!this.selectedVideo) {
          this.errors.video = 'Video selection is required';
          hasErrors = true;
        }
        
        if (hasErrors) {
          // Scroll to the first error
          const firstError = document.querySelector('.has-error');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
        
        this.isSubmitting = true;
        const token = localStorage.getItem('token');
        
        const formData = new FormData();
        
        // Add all form fields to formData
        formData.append('title', title);
        formData.append('description', this.form.description);
        formData.append('course_id', this.form.course_id);
        formData.append('lesson_video_id', this.selectedVideo?.videoId || '');
        formData.append('lesson_url', this.selectedVideo?.assets?.hls || '');
        formData.append('lesson_thumbnail_url', this.selectedVideo?.assets?.thumbnail || '');
        formData.append('downloadable_url', this.selectedVideo?.assets?.mp4 || '');
        formData.append('lesson_status', 'pending');
        formData.append('order', parseInt(this.form.order) || 0);
        formData.append('duration', parseInt(this.form.duration) || 3600);
        formData.append('is_hidden', this.form.is_hidden ? 1 : 0);
        formData.append('contents', this.descriptionEditor.getHTML());
        formData.append('premium_course_id', this.form.course_id);
        formData.append('title_ar', this.form.title_ar);
        formData.append('description_ar', this.form.description_ar);
        
        if (this.selectedVideo?.assets?.thumbnail) {
          formData.append('lesson_temp_file', this.selectedVideo.assets.thumbnail);
        }

        await axios.post('https://api.kewaar.com/api/admin/lessons1', formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'multipart/form-data'
          }
        });
        
        this.$router.push('/lessons');
      } catch (error) {
        console.error('Error creating lesson:', error);
        
        if (error.response?.data?.message) {
          // Show error message to user
          alert(`Error: ${error.response.data.message}`);
        } else {
          alert('An error occurred while creating the lesson. Please try again.');
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    clearForm() {
      this.form = {
        title: '',
        description: '',
        course_id: '',
        lesson_video_id: '',
        lesson_url: '',
        lesson_thumbnail_url: '',
        lesson_status: 'uploaded',
        order: 1,
        duration: 3600,
        is_hidden: 0,
        downloadable_url: '',
        premium_course_id: '',
        title_ar: '',
        description_ar: '',
      };
      
      if (this.titleEditor) {
        this.titleEditor.commands.clearContent(true);
      }
      if (this.descriptionEditor) {
        this.descriptionEditor.commands.clearContent(true);
      }
      
      this.courseSearchTerm = '';
      this.videoSearchTerm = '';
    }
  }
};
</script>

<style scoped>
.lesson-create-container {
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
  max-width: 800px;
}

.create-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.back-button {
  background: #f1f5f9;
  color: #1e40af;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  font-weight: 500;
}

.back-button:hover {
  background: #e0e7ff;
  transform: translateY(-1px);
}

.back-button svg {
  width: 20px;
  height: 20px;
  stroke-width: 1.5;
}

.lesson-form {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
}

.thumbnail-preview {
  margin-top: 1rem;
  max-width: 200px;
  border-radius: 8px;
  overflow: hidden;
}

.thumbnail-preview img {
  width: 100%;
  height: auto;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.clear-button,
.cancel-button,
.submit-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.clear-button {
  background: #fef2f2;
  color: #dc2626;
}

.clear-button:hover {
  background: #fee2e2;
}

.cancel-button {
  background: #f1f5f9;
  color: #64748b;
}

.cancel-button:hover {
  background: #e2e8f0;
}

.submit-button {
  background: linear-gradient(to right, #2563eb, #1d4ed8);
  color: white;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(29, 78, 216, 0.2);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.form-group.has-error .form-input,
.form-group.has-error .editor-title,
.form-group.has-error .editor-container,
.form-group.has-error .vs__dropdown-toggle {
  border-color: #dc2626;
}

.error-message {
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.required {
  color: #dc2626;
  margin-left: 0.25rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.editor-title,
.editor-container {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.editor-title:focus,
.editor-container:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.editor-menu {
  padding: 0.75rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 0.75rem;
  background: #f8fafc;
  border-radius: 0 0 8px 8px;
}

.editor-menu button {
  padding: 0.5rem;
  border-radius: 6px;
  background: white;
  border: 1px solid #e2e8f0;
  color: #64748b;
  transition: all 0.2s ease;
}

.editor-menu button:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.editor-menu button.is-active {
  background: #2563eb;
  color: white;
  border-color: #2563eb;
}

.video-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
}

.video-thumbnail {
  width: 100px;
  height: 56px;
  object-fit: cover;
  border-radius: 6px;
}

.video-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: #64748b;
}

.course-selector,
.video-selector {
  width: 100%;
}

.vs__dropdown-toggle {
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.vs__dropdown-toggle:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.vs__selected {
  margin: 0;
  padding: 0.25rem 0.5rem;
  font-size: 1rem;
  color: #1e293b;
}

.vs__search {
  padding: 0.25rem 0.5rem;
  font-size: 1rem;
  color: #1e293b;
}

.vs__dropdown-menu {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-top: 0.5rem;
}

.vs__dropdown-option {
  padding: 0.75rem;
}

.vs__dropdown-option--highlight {
  background: #2563eb;
  color: white;
}

@media (max-width: 768px) {
  .lesson-create-container {
    margin-left: 0;
    padding: 1rem;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .clear-button,
  .cancel-button,
  .submit-button {
    width: 100%;
    justify-content: center;
  }
}
</style> 